<script setup lang="ts">
/**
 * Offers the visitor their country's language, once.
 *
 * Cloudflare knows which country a request came from, and for somebody who
 * has not signed in and whose browser is set to a language we do not publish
 * in, that is the only thing we know about them. The obvious move is to
 * switch the interface to it outright. This deliberately does not.
 *
 * A browser set to English is somebody saying they read English. A Thai
 * address is a fact about a network: it is equally true of a Thai speaker
 * whose phone shipped in English, and of a visitor from abroad who has been
 * in the country for three days. Overriding the first person's setting to
 * help them means overriding the second person's to hurt them, and neither
 * of them asked. So this asks, takes the answer, and never asks again.
 *
 * **Written in the language being offered, not the one on screen.** An
 * invitation to read in Thai, written in English, is addressed to precisely
 * the person who cannot read it. `translateIn` exists for this.
 *
 * It is `fixed`, so it cannot shift the page it appears over, and it renders
 * nothing at all until every condition holds — which is most of the time.
 */
import { computed, onMounted, ref } from 'vue';
import { languageForCountry } from '@talkvibe/shared';

import NavIcon from './NavIcon.vue';
import { detectCountry } from '../services/geo.ts';
import { countryName, languageName } from '../services/translation.ts';
import { useAuthStore } from '../stores/auth.ts';
import {
  chooseLocale,
  locale,
  rememberLocale,
  intlLocale,
  resolveLocale,
  storedLocale,
  translateIn,
  type Locale,
} from '../i18n/index.ts';

const auth = useAuthStore();

/** The country Cloudflare reported, once it has answered. */
const country = ref<string | null>(null);
/** Set the moment the offer is answered, so it goes away without a reload. */
const answered = ref(false);

onMounted(async () => {
  // Someone signed in has a language on their account; that is their answer
  // already, saved deliberately and shared across their devices.
  if (auth.isAuthenticated) return;
  // A choice in the header, or an earlier answer to this very question.
  if (storedLocale() !== null) return;
  country.value = await detectCountry();
});

/** The catalogue this country suggests, or null when there is nothing to offer. */
const suggestion = computed<Locale | null>(() => {
  if (answered.value || country.value === null) return null;
  const language = languageForCountry(country.value);
  if (language === null) return null;

  // `resolveLocale` answers English for every language we have no catalogue
  // for, so the answer has to be checked against what was asked. Without
  // this, a visitor in Japan would be offered English on the strength of a
  // Japanese address — a suggestion nothing in their request supports.
  const target = resolveLocale(language);
  if (target !== language && target !== language.split('-')[0]) return null;

  return target === locale.value ? null : target;
});

const text = computed(() => {
  const target = suggestion.value;
  if (target === null || country.value === null) return null;
  return {
    label: translateIn(target, 'geo.suggestLabel'),
    title: translateIn(target, 'geo.suggestTitle', {
      country: countryName(country.value, target),
    }),
    body: translateIn(target, 'geo.suggestBody', { language: languageName(target, target) }),
    accept: translateIn(target, 'geo.suggestAccept', { language: languageName(target, target) }),
    dismiss: translateIn(target, 'geo.suggestDismiss'),
  };
});

function accept(): void {
  const target = suggestion.value;
  answered.value = true;
  if (target !== null) chooseLocale(target);
}

function decline(): void {
  answered.value = true;
  // Storing what they already have is what records that the question was
  // asked and settled — the same key the header switcher writes.
  rememberLocale(intlLocale.value);
}
</script>

<template>
  <div
    v-if="text"
    class="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-gutter pb-space-md"
  >
    <div
      class="pointer-events-auto flex w-full max-w-md flex-col gap-space-sm rounded-card bg-surface-container-lowest p-space-md shadow-xl ring-1 ring-outline-variant"
      role="region"
      :aria-label="text.label"
    >
      <div class="flex items-start gap-space-sm">
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-container text-primary">
          <NavIcon name="translate" class="h-5 w-5" />
        </span>
        <p class="text-body-md text-on-surface">
          {{ text.title }}<br />
          <span class="text-on-surface-variant">{{ text.body }}</span>
        </p>
      </div>

      <div class="flex items-center justify-end gap-space-sm">
        <button
          type="button"
          class="rounded-full px-space-md py-2 text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
          @click="decline"
        >
          {{ text.dismiss }}
        </button>
        <button
          type="button"
          class="rounded-full bg-primary-container px-space-lg py-2 text-label-md text-on-primary shadow-sm transition-shadow hover:shadow-md"
          @click="accept"
        >
          {{ text.accept }}
        </button>
      </div>
    </div>
  </div>
</template>
